import Ionicons from "@expo/vector-icons/Ionicons";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  type StyleProp,
  Switch,
  TextInput,
  type TextStyle,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { goalsApi, metricsApi, reflectionsApi } from "@/api";
import {
  formatGoalDueDate,
  GoalDuePicker,
  type GoalDuePickerHandle,
} from "@/components/growth/goal-due-picker";
import { ReminderTimePicker } from "@/components/growth/reminder-time-picker";
import {
  GoalsProgressDetail,
  PracticeProgressDetail,
  ProgressDetailHeader,
  type ProgressDetailKey,
  ProgressStoryGrid,
  ReflectionsProgressDetail,
} from "@/components/growth/progress-story";
import { ReflectionDetailModal } from "@/components/growth/reflection-detail-modal";
import { ReflectionHistoryList } from "@/components/growth/reflection-history-list";
import { useNavigation } from "expo-router";
import { ReflectionModal } from "@/components/growth/reflection-modal";
import { ShareProgressCard } from "@/components/growth/share-progress-card";
import { TrendChart } from "@/components/growth/trend-chart";
import { ProfileVibeCardEntry } from "@/components/profile/profile-vibe-card-entry";
import { PlusFeatureUpsell } from "@/components/plus-feature-upsell";
import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import {
  KeyboardAvoidingContainer,
  KeyboardScrollView,
} from "@/components/ui/keyboard-view";
import { Pager, usePagedItems } from "@/components/ui/pager";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { confirm, notify } from "@/lib/dialog";
import { shareText } from "@/lib/share-text";
import { activeGoalLimit, goalSlotsFullCopy } from "@/lib/goal-limits";
import { syncGoalReminders } from "@/lib/goal-reminder";
import {
  type GoalAction,
  GOAL_NOTE_MAX_COUNT,
  GOAL_NOTE_MAX_LENGTH,
  GOAL_TEXT_MAX_LENGTH,
  goalMenuActions,
  isGoalActionDisabled,
  goalsForTab,
  isGoalOverdue,
  sanitizeGoalNoteDraft,
  sanitizeGoalNotes,
} from "@/lib/goals-view";
import {
  reflectionFeelingLabel,
  reflectionOutcomeLabel,
  reflectionSubjectLabel,
  reflectionWhereLabel,
} from "@/lib/reflection-format";
import { reflectionQuotaIntro } from "@/lib/reflection-limits";
import { syncReflectionReminder } from "@/lib/reflection-reminder";
import { storage } from "@/lib/storage";
import i18n from "@/i18n";
import {
  readDashboardCache,
  writeDashboardCache,
} from "@/lib/dashboard-cache";
import {
  persistDashboardControls,
  readDashboardControls,
} from "@/lib/dashboard-controls";
import { takeDashboardPrefetch } from "@/lib/dashboard-prefetch";
import { useAuthStore } from "@/state/auth-store";
import { exportRequestFor } from "@/lib/export-request";
import type {
  ExportRange,
  ProgressDashboard,
  Reflection,
  ReflectionExport,
  ReflectionFrequency,
  ReflectionQuota,
  SocialGoal,
  TrendPoint,
} from "@/types";

const FREQUENCY_OPTIONS: { value: ReflectionFrequency; label: string }[] = [
  {
    value: "evenings",
    get label() {
      return i18n.t("growth.dashboard.frequency.evenings");
    },
  },
  {
    value: "weekly",
    get label() {
      return i18n.t("growth.dashboard.frequency.weekly");
    },
  },
  {
    value: "custom",
    get label() {
      return i18n.t("growth.dashboard.frequency.custom");
    },
  },
  {
    value: "off",
    get label() {
      return i18n.t("growth.dashboard.frequency.off");
    },
  },
];

/** Weekday chips for the custom reminder, in display order (Sun … Sat). */
const CUSTOM_WEEKDAYS: { value: number; get label(): string }[] = [
  { value: 0, get label() { return i18n.t("growth.dashboard.weekday.sun"); } },
  { value: 1, get label() { return i18n.t("growth.dashboard.weekday.mon"); } },
  { value: 2, get label() { return i18n.t("growth.dashboard.weekday.tue"); } },
  { value: 3, get label() { return i18n.t("growth.dashboard.weekday.wed"); } },
  { value: 4, get label() { return i18n.t("growth.dashboard.weekday.thu"); } },
  { value: 5, get label() { return i18n.t("growth.dashboard.weekday.fri"); } },
  { value: 6, get label() { return i18n.t("growth.dashboard.weekday.sat"); } },
];

/** Default custom reminder time when the user hasn't picked one yet. */
const DEFAULT_REMINDER_TIME = "20:00";

function formatProgressRange(start: string, end: string): string {
  const formatter = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${formatter.format(new Date(start))} - ${formatter.format(
    new Date(end),
  )}`;
}

function colorWithAlpha(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  if (!/^[\da-f]{6}$/i.test(normalized)) return hex;
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

type ExportFormat = "json" | "csv" | "pdf";
// `ExportRange` is imported from @/types rather than redeclared here: the
// server now applies the range, so a second local copy could drift from the
// one the request is validated against.
type GoalTab = "active" | "completed" | "archived";
type PlusGateKind = "goals" | "monthly" | "export" | "poster";

/**
 * The goal composer, used both for "+ New goal" and for editing an existing
 * one. `focusDue` opens the due-date picker straight away, for the overdue
 * row's "Change due date" action.
 */
type GoalSheetState =
  | { mode: "create" }
  | { mode: "edit"; goal: SocialGoal; focusDue?: boolean };

const GOAL_ACTION_ICONS: Record<GoalAction, keyof typeof Ionicons.glyphMap> = {
  complete: "checkmark-circle-outline",
  reopen: "refresh-outline",
  edit: "create-outline",
  changeDue: "calendar-outline",
  archive: "archive-outline",
  restore: "arrow-undo-outline",
  delete: "trash-outline",
};
type ExportSections = {
  growthStats: boolean;
  weeklySummaries: boolean;
  reflections: boolean;
  privateNotes: boolean;
};

const EXPORT_FORMATS: {
  value: ExportFormat;
  label: string;
  disabled?: boolean;
}[] = [
  {
    value: "json",
    get label() {
      return i18n.t("growth.dashboard.format.json");
    },
  },
  {
    value: "csv",
    get label() {
      return i18n.t("growth.dashboard.format.csv");
    },
  },
  {
    value: "pdf",
    get label() {
      return i18n.t("growth.dashboard.format.pdfSoon");
    },
    disabled: true,
  },
];

const EXPORT_RANGES: { value: ExportRange; label: string }[] = [
  {
    value: "last30",
    get label() {
      return i18n.t("growth.dashboard.range.thirtyDays");
    },
  },
  {
    value: "last12months",
    get label() {
      return i18n.t("growth.dashboard.range.twelveMonths");
    },
  },
  {
    value: "all",
    get label() {
      return i18n.t("growth.dashboard.range.all");
    },
  },
];

/** Trigger a browser download of a text payload (web only). */
function downloadText(filename: string, body: string, type: string): void {
  const g = globalThis as unknown as {
    Blob?: new (parts: string[], opts?: { type?: string }) => unknown;
    URL?: {
      createObjectURL(blob: unknown): string;
      revokeObjectURL(url: string): void;
    };
    document?: {
      createElement(tag: "a"): {
        href: string;
        download: string;
        click(): void;
      };
    };
  };
  if (!g.Blob || !g.URL || !g.document) return;
  const url = g.URL.createObjectURL(new g.Blob([body], { type }));
  const a = g.document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  g.URL.revokeObjectURL(url);
}

function isoDate(value: string): string {
  return value.slice(0, 10);
}

function rangeLabel(range: ExportRange): string {
  return (
    EXPORT_RANGES.find((item) => item.value === range)?.label ??
    i18n.t("growth.dashboard.range.all")
  );
}

function startForRange(range: ExportRange): Date | null {
  if (range === "all") return null;
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - (range === "last30" ? 29 : 364));
  return date;
}

function inExportRange(iso: string, range: ExportRange): boolean {
  const start = startForRange(range);
  if (!start) return true;
  return new Date(iso) >= start;
}

function shortDateLabel(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function weekLabel(weekStart: string): string {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return `${shortDateLabel(start)}-${shortDateLabel(end)}`;
}

function showedUpDays(reflections: Reflection[]): number {
  return new Set(reflections.map((item) => isoDate(item.createdAt))).size;
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function csvRow(values: unknown[]): string {
  return values.map(csvCell).join(",");
}

function buildWeeklySummaries(points: TrendPoint[], range: ExportRange) {
  return points
    .filter((point) => inExportRange(point.weekStart, range))
    .map((point) => ({
      week: weekLabel(point.weekStart),
      reflections: point.reflections,
      conversationsStarted: point.conversationsStarted,
      groupsJoined: point.groupsJoined,
      message: i18n.t("growth.smallStepsCount"),
    }));
}

function buildExportObject(
  data: ReflectionExport,
  range: ExportRange,
  sections: ExportSections,
) {
  const reflections = data.reflections.filter((item) =>
    inExportRange(item.createdAt, range),
  );
  const weeklySummaries = buildWeeklySummaries(
    data.weeklySummaries ?? [],
    range,
  );
  const weeklyTotals = weeklySummaries.reduce(
    (total, week) => ({
      conversationsStarted:
        total.conversationsStarted + week.conversationsStarted,
      groupsJoined: total.groupsJoined + week.groupsJoined,
    }),
    { conversationsStarted: 0, groupsJoined: 0 },
  );
  const payload: Record<string, unknown> = {
    readMe: {
      title: i18n.t("growth.exportData.title"),
      privacyNote: i18n.t("growth.exportData.privacyNote"),
      howToUse: i18n.t("growth.exportData.howToUse"),
    },
    generatedAt: data.generatedAt,
  };

  // Say so in the file itself, not only in a toast the person can miss. An
  // export that quietly stops at the cap would look complete forever after.
  if (data.reflectionsTruncated && sections.reflections) {
    (payload.readMe as Record<string, unknown>).truncated = i18n.t(
      "growth.exportData.truncatedNote",
    );
  }

  if (sections.growthStats) {
    payload.summary = {
      period: rangeLabel(range),
      reflections: reflections.length,
      // `totals` is null only when the section was not requested, which cannot
      // happen inside this branch — but fall back to the weekly figures rather
      // than crash the export if the server ever disagrees.
      conversationsStarted:
        range === "last30" || !data.totals
          ? weeklyTotals.conversationsStarted
          : data.totals.conversationsStarted,
      groupsJoined:
        range === "last30" || !data.totals
          ? weeklyTotals.groupsJoined
          : data.totals.groupsJoined,
      daysShowedUp: showedUpDays(reflections),
    };
  }

  if (sections.reflections) {
    payload.reflections = reflections.map((item) => ({
      date: isoDate(item.createdAt),
      type:
        (item.subject && reflectionSubjectLabel(item.subject)) ||
        (item.where
          ? reflectionWhereLabel(item.where)
          : i18n.t("growth.reflection")),
      feeling: (item.feeling && reflectionFeelingLabel(item.feeling)) || null,
      outcome: (item.outcome && reflectionOutcomeLabel(item.outcome)) || null,
      note: sections.privateNotes ? (item.note ?? null) : null,
    }));
  }

  if (sections.weeklySummaries) {
    payload.weeklySummaries = weeklySummaries;
  }

  return payload;
}

function buildExportCsv(payload: Record<string, unknown>): string {
  const rows: string[] = [
    csvRow([
      "section",
      "field",
      "value",
      "date",
      "type",
      "feeling",
      "outcome",
      "note",
    ]),
  ];
  const readMe = payload.readMe as Record<string, unknown> | undefined;
  if (readMe) {
    Object.entries(readMe).forEach(([field, value]) => {
      rows.push(csvRow(["readMe", field, value, "", "", "", "", ""]));
    });
  }

  const summary = payload.summary as Record<string, unknown> | undefined;
  if (summary) {
    Object.entries(summary).forEach(([field, value]) => {
      rows.push(csvRow(["summary", field, value, "", "", "", "", ""]));
    });
  }

  const reflections = payload.reflections as
    | Record<string, unknown>[]
    | undefined;
  reflections?.forEach((item) => {
    rows.push(
      csvRow([
        "reflection",
        "",
        "",
        item.date,
        item.type,
        item.feeling,
        item.outcome,
        item.note,
      ]),
    );
  });

  const weeklySummaries = payload.weeklySummaries as
    | Record<string, unknown>[]
    | undefined;
  weeklySummaries?.forEach((item) => {
    rows.push(
      csvRow([
        "weeklySummary",
        item.week,
        `reflections=${item.reflections}; conversationsStarted=${item.conversationsStarted}; groupsJoined=${item.groupsJoined}; message=${item.message}`,
        "",
        "",
        "",
        "",
        "",
      ]),
    );
  });

  return rows.join("\n");
}

export function GrowthDashboardModal({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  useTranslation();
  const c = useTheme();
  const sectionActionHeaderColors = {
    backgroundColor: colorWithAlpha(c.backgroundSelected, 0.52),
    borderColor: colorWithAlpha(c.tint, 0.46),
  };
  const reflectionFrequency = useAuthStore(
    (s) => s.user?.reflectionFrequency ?? "evenings",
  );
  const reflectionReminderDays = useAuthStore(
    (s) => s.user?.reflectionReminderDays,
  );
  const reflectionReminderTime = useAuthStore(
    (s) => s.user?.reflectionReminderTime,
  );
  const setProfile = useAuthStore((s) => s.setProfile);
  const weeklyEmailOptIn = useAuthStore(
    (s) => s.user?.weeklyEmailOptIn ?? true,
  );
  const userPlan = useAuthStore((s) => s.user?.plan);
  const userId = useAuthStore((s) => s.user?.id);
  const notificationPreferences = useAuthStore(
    (s) => s.user?.notificationPreferences ?? null,
  );
  const [selectedReflection, setSelectedReflection] =
    useState<Reflection | null>(null);
  // Bumped whenever a reflection is logged or deleted, so the history list
  // refetches its first page instead of holding a stale one. The list owns its
  // own paging, so handing it rows from here would fight that.
  const [reflectionsVersion, setReflectionsVersion] = useState(0);
  const [quota, setQuota] = useState<ReflectionQuota | null>(null);
  const [progress, setProgress] = useState<ProgressDashboard | null>(null);
  const [detail, setDetail] = useState<ProgressDetailKey | null>(null);
  const [goals, setGoals] = useState<SocialGoal[]>([]);
  const [goalsOpen, setGoalsOpen] = useState(false);
  const [reflectionRemindersOpen, setReflectionRemindersOpen] =
    useState(false);
  const [goalTab, setGoalTab] = useState<GoalTab>("active");
  const [goalSheet, setGoalSheet] = useState<GoalSheetState | null>(null);
  // Which goal's overflow (⋯) menu is open.
  const [goalMenu, setGoalMenu] = useState<SocialGoal | null>(null);
  const [goalTitleDraft, setGoalTitleDraft] = useState("");
  const [goalTitleMissing, setGoalTitleMissing] = useState(false);
  const [goalDueDraft, setGoalDueDraft] = useState<string | null>(null);
  const [goalNoteDrafts, setGoalNoteDrafts] = useState<string[]>([""]);
  const [savingGoal, setSavingGoal] = useState(false);
  const [undoGoal, setUndoGoal] = useState<SocialGoal | null>(null);
  const undoGoalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Lets "Change due date" pop the picker as the composer opens.
  const dueRef = useRef<GoalDuePickerHandle>(null);
  const [loading, setLoading] = useState(true);
  const [reflectOpen, setReflectOpen] = useState(false);
  const [range, setRange] = useState<7 | 30>(7);
  const [dashboardControlsOpen, setDashboardControlsOpen] = useState(false);
  const [metricsResetAt, setMetricsResetAt] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportOptionsOpen, setExportOptionsOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("json");
  const [exportRange, setExportRange] = useState<ExportRange>("last30");
  const [exportSections, setExportSections] = useState<ExportSections>({
    growthStats: true,
    weeklySummaries: true,
    reflections: true,
    privateNotes: false,
  });
  const [plusGate, setPlusGate] = useState<PlusGateKind | null>(null);

  // The quota response is the source of truth for plan; fall back to the store.
  const plan = quota?.plan ?? userPlan;
  const isPlus = plan === "plus";
  // The Completed and Archived tabs are the user's whole history — the
  // Week/Month selector above only moves the metrics, never these lists.
  const activeGoals = goalsForTab(goals, "active");
  const completedGoals = goalsForTab(goals, "completed");
  const archivedGoals = goalsForTab(goals, "archived");
  const goalLimit = activeGoalLimit(plan);
  const atGoalLimit = activeGoals.length >= goalLimit;
  // Whichever tab is open is shown five at a time: someone with 30 completed
  // goals should not have to scroll past all 30 to reach the rest of the sheet.
  const tabGoals =
    goalTab === "active"
      ? activeGoals
      : goalTab === "completed"
        ? completedGoals
        : archivedGoals;
  const {
    page: goalPage,
    totalPages: goalPages,
    visible: visibleGoals,
    setPage: setGoalPage,
  } = usePagedItems(tabGoals);
  const emptyGoalsCopy =
    goalTab === "active"
      ? i18n.t("growth.dashboard.noGoals")
      : goalTab === "completed"
        ? i18n.t("growth.dashboard.completedEmpty")
        : i18n.t("growth.dashboard.archivedEmpty");
  const closeCurrentViewA11y = detail
    ? "Back to progress overview"
    : i18n.t("growth.dashboard.closeA11y");
  const plusGateCopy =
    plusGate === "goals"
      ? {
          title: i18n.t("growth.dashboard.plusGate.goalsTitle"),
          body: i18n.t("growth.dashboard.plusGate.goalsBody"),
          body2: i18n.t("growth.dashboard.plusGate.sharedBody2"),
          trigger: "free_limit_reached" as const,
        }
      : plusGate === "monthly"
        ? {
            title: i18n.t("growth.dashboard.plusGate.monthlyTitle"),
            intro: i18n.t("growth.dashboard.plusGate.monthlyBody"),
            body: i18n.t("growth.dashboard.plusGate.sharedBody"),
            body2: i18n.t("growth.dashboard.plusGate.sharedBody2"),
            trigger: "plus_feature_attempted" as const,
          }
        : plusGate === "export"
          ? {
              title: i18n.t("growth.dashboard.plusGate.exportTitle"),
              body: i18n.t("growth.dashboard.plusGate.sharedBody"),
              body2: i18n.t("growth.dashboard.plusGate.sharedBody2"),
              trigger: "plus_feature_attempted" as const,
            }
          : plusGate === "poster"
            ? {
                title: i18n.t("growth.dashboard.plusGate.posterTitle"),
                intro: i18n.t("growth.dashboard.plusGate.posterBody"),
                body: i18n.t("growth.dashboard.plusGate.sharedBody"),
                body2: i18n.t("growth.dashboard.plusGate.sharedBody2"),
                trigger: "plus_feature_attempted" as const,
              }
            : null;

  function openPlusGate(kind: PlusGateKind) {
    setPlusGate(kind);
  }

  function showGoalLimit() {
    if (plan !== "plus") {
      openPlusGate("goals");
      return;
    }
    notify(
      i18n.t("growth.dashboard.goalsFull"),
      goalSlotsFullCopy(activeGoals.length),
    );
  }

  /** Tabs always open on page 1 — a page 4 from another tab means nothing here. */
  function showGoalTab(tab: GoalTab) {
    setGoalTab(tab);
    setGoalPage(1);
  }

  function closeCurrentView() {
    if (detail) {
      setDetail(null);
      return;
    }
    onClose();
  }

  async function refreshMetrics(nextRange: 7 | 30, resetAt: string | null) {
    setProgress(await metricsApi.progress(nextRange, resetAt));
  }

  async function changeRange(next: 7 | 30) {
    if (next === range) return;
    setRange(next);

    // Week and Month are two different questions, so each is cached under its
    // own key. Only `progress` is range-scoped — goals and the quota describe
    // the account, not the window — so a cached entry is read for its chart
    // alone, and the fetch below still runs to replace it.
    const cached = readDashboardCache(userId, next, metricsResetAt);
    if (cached) setProgress(cached.progress);

    try {
      const nextProgress = await metricsApi.progress(next, metricsResetAt);
      setProgress(nextProgress);
      // Only once there is a quota to store with it: the snapshot is one
      // object, and half of one would be a cache entry that lies about what
      // it holds.
      if (quota) {
        writeDashboardCache(userId, next, metricsResetAt, {
          progress: nextProgress,
          goals,
          quota,
        });
      }
    } catch {
      // Keep the previous summary/trends on failure.
    }
  }

  async function resetDashboardMetrics() {
    const resetAt = new Date().toISOString();
    setMetricsResetAt(resetAt);
    setDashboardControlsOpen(false);
    await persistDashboardControls({ resetAt });
    try {
      await refreshMetrics(range, resetAt);
    } catch {
      // Keep the previous metrics on failure; the reset preference is saved.
    }
  }

  async function handleExport() {
    if (exporting) return;
    if (exportFormat === "pdf") {
      notify(
        i18n.t("growth.dashboard.pdfSoonTitle"),
        i18n.t("growth.dashboard.pdfSoonBody"),
      );
      return;
    }
    if (
      !exportSections.growthStats &&
      !exportSections.weeklySummaries &&
      !exportSections.reflections
    ) {
      notify(
        i18n.t("growth.dashboard.chooseExportTitle"),
        i18n.t("growth.dashboard.chooseExportBody"),
      );
      return;
    }
    setExporting(true);
    try {
      const data = await reflectionsApi.export(
        exportRequestFor(exportRange, exportSections),
      );
      const payload = buildExportObject(data, exportRange, exportSections);
      const isCsv = exportFormat === "csv";
      const body = isCsv
        ? buildExportCsv(payload)
        : JSON.stringify(payload, null, 2);
      const extension = isCsv ? "csv" : "json";
      const mime = isCsv ? "text/csv" : "application/json";
      const filename = `opncue-progress-${exportRange}-${data.generatedAt.slice(
        0,
        10,
      )}.${extension}`;
      if (Platform.OS === "web") {
        downloadText(filename, body, mime);
      } else {
        await shareText({ title: filename, message: body });
      }
      if (data.reflectionsTruncated && exportSections.reflections) {
        notify(
          i18n.t("growth.dashboard.exportTruncatedTitle"),
          i18n.t("growth.dashboard.exportTruncatedBody"),
        );
      }
    } catch {
      notify(
        i18n.t("growth.dashboard.exportUnavailableTitle"),
        i18n.t("growth.dashboard.exportUnavailableBody"),
      );
    } finally {
      setExporting(false);
    }
  }

  function toggleExportSection(key: keyof ExportSections) {
    setExportSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  const customDays = reflectionReminderDays ?? [];
  const customTime = reflectionReminderTime ?? DEFAULT_REMINDER_TIME;

  async function changeFrequency(value: ReflectionFrequency) {
    if (value === reflectionFrequency) return;
    try {
      if (value === "custom") {
        // Seed a sensible default so a freshly-picked "custom" fires something.
        await setProfile({
          reflectionFrequency: value,
          reflectionReminderDays:
            customDays.length > 0 ? customDays : [1, 3, 5],
          reflectionReminderTime: reflectionReminderTime ?? DEFAULT_REMINDER_TIME,
        });
      } else {
        await setProfile({ reflectionFrequency: value });
      }
    } catch {
      // Non-critical; the toggle simply won't move.
    }
  }

  async function toggleCustomDay(day: number) {
    const next = customDays.includes(day)
      ? customDays.filter((d) => d !== day)
      : [...customDays, day].sort((a, b) => a - b);
    try {
      await setProfile({ reflectionReminderDays: next });
    } catch {
      // Ignore — the chip just won't move.
    }
  }

  async function changeCustomTime(time: string) {
    try {
      await setProfile({ reflectionReminderTime: time });
    } catch {
      // Ignore — the picker just won't move.
    }
  }

  async function toggleWeeklyEmail(value: boolean) {
    try {
      await setProfile({ weeklyEmailOptIn: value });
    } catch {
      // Ignore — the switch just won't move.
    }
  }

  function toggleGoal(goal: SocialGoal) {
    return setGoalDone(goal, !goal.done);
  }

  /**
   * The single path a goal takes to done and back, whether it was ticked on the
   * row or chosen from the overflow menu. Takes the target state rather than
   * flipping, so "Mark as complete" always completes — it can never reopen a
   * goal that finished after the menu was opened.
   */
  async function setGoalDone(goal: SocialGoal, nextDone: boolean) {
    if (goal.archived || goal.done === nextDone) return;
    setGoals((prev) =>
      prev.map((g) =>
        g.id === goal.id
          ? {
              ...g,
              done: nextDone,
              completedAt: nextDone ? new Date().toISOString() : null,
            }
          : g,
      ),
    );
    try {
      const updated = await goalsApi.setDone(goal.id, nextDone);
      setGoals((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      if (nextDone) {
        if (undoGoalTimer.current) clearTimeout(undoGoalTimer.current);
        setUndoGoal(updated);
        undoGoalTimer.current = setTimeout(() => setUndoGoal(null), 5000);
        showGoalTab("completed");
      } else {
        setUndoGoal(null);
      }
      await refreshMetrics(range, metricsResetAt);
    } catch {
      void load(metricsResetAt);
    }
  }

  async function undoCompletedGoal() {
    if (!undoGoal) return;
    const goal = undoGoal;
    setUndoGoal(null);
    if (undoGoalTimer.current) clearTimeout(undoGoalTimer.current);
    try {
      const updated = await goalsApi.setDone(goal.id, false);
      setGoals((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      showGoalTab("active");
      await refreshMetrics(range, metricsResetAt);
    } catch {
      void load(metricsResetAt);
    }
  }

  async function archiveGoal(goal: SocialGoal) {
    if (goal.archived) return;
    const archivedAt = new Date().toISOString();
    setGoals((prev) =>
      prev.map((item) =>
        item.id === goal.id ? { ...item, archived: true, archivedAt } : item,
      ),
    );
    try {
      const archived = await goalsApi.archive(goal.id);
      setGoals((prev) =>
        prev.map((item) => (item.id === archived.id ? archived : item)),
      );
      await refreshMetrics(range, metricsResetAt);
    } catch {
      void load(metricsResetAt);
    }
  }

  /** Permanent, so it always asks first — there is no undo for a delete. */
  async function removeGoal(goal: SocialGoal) {
    const confirmed = await confirm({
      title: i18n.t("growth.dashboard.deleteGoalTitle"),
      message: i18n.t("growth.dashboard.deleteGoalBody", { goal: goal.text }),
      confirmLabel: i18n.t("growth.dashboard.delete"),
      cancelLabel: i18n.t("growth.dashboard.cancel"),
      destructive: true,
    });
    if (!confirmed) return;
    setGoals((prev) => prev.filter((g) => g.id !== goal.id));
    try {
      await goalsApi.remove(goal.id);
      await refreshMetrics(range, metricsResetAt);
    } catch {
      void load(metricsResetAt);
    }
  }

  async function restoreGoal(goal: SocialGoal) {
    try {
      const restored = await goalsApi.restore(goal.id);
      setGoals((prev) =>
        prev.map((item) => (item.id === restored.id ? restored : item)),
      );
      showGoalTab(restored.done ? "completed" : "active");
      await refreshMetrics(range, metricsResetAt);
    } catch (err) {
      if (err instanceof Error && /goal_slots_full/i.test(err.message)) {
        showGoalLimit();
        return;
      }
      void load(metricsResetAt);
    }
  }

  /** Open the composer, either empty or filled in from an existing goal. */
  function openGoalSheet(state: GoalSheetState) {
    const goal = state.mode === "edit" ? state.goal : null;
    const notes = (goal?.notes ?? []).slice(0, GOAL_NOTE_MAX_COUNT);
    setGoalTitleDraft(goal?.text ?? "");
    setGoalDueDraft(goal?.dueDate ?? null);
    setGoalNoteDrafts(notes.length > 0 ? notes : [""]);
    setGoalTitleMissing(false);
    setSavingGoal(false);
    setGoalSheet(state);
  }

  function closeGoalSheet() {
    if (savingGoal) return;
    setGoalSheet(null);
    setGoalTitleDraft("");
    setGoalDueDraft(null);
    setGoalNoteDrafts([""]);
    setGoalTitleMissing(false);
  }

  function updateGoalNote(index: number, value: string) {
    const cleaned = sanitizeGoalNoteDraft(value);
    setGoalNoteDrafts((current) =>
      current.map((note, i) => (i === index ? cleaned : note)),
    );
  }

  function addGoalNoteInput() {
    setGoalNoteDrafts((current) =>
      current.length >= GOAL_NOTE_MAX_COUNT ? current : [...current, ""],
    );
  }

  function removeGoalNoteInput(index: number) {
    setGoalNoteDrafts((current) => {
      const next = current.filter((_, i) => i !== index);
      return next.length > 0 ? next : [""];
    });
  }

  /** Save the composer. Only the title is required; due date and notes aren't. */
  async function saveGoalSheet() {
    if (!goalSheet || savingGoal) return;
    const text = goalTitleDraft.trim().slice(0, GOAL_TEXT_MAX_LENGTH);
    if (!text) {
      setGoalTitleMissing(true);
      return;
    }
    const notes = sanitizeGoalNotes(goalNoteDrafts);
    setSavingGoal(true);
    try {
      if (goalSheet.mode === "create") {
        const created = await goalsApi.create(text, goalDueDraft, notes);
        setGoals((prev) => [created, ...prev]);
        showGoalTab("active");
      } else {
        const updated = await goalsApi.update(goalSheet.goal.id, {
          text,
          dueDate: goalDueDraft,
          notes,
        });
        setGoals((prev) =>
          prev.map((goal) => (goal.id === updated.id ? updated : goal)),
        );
      }
      setGoalSheet(null);
      setGoalTitleDraft("");
      setGoalDueDraft(null);
      setGoalNoteDrafts([""]);
      await refreshMetrics(range, metricsResetAt);
    } catch (err) {
      if (err instanceof Error && /goal_slots_full/i.test(err.message)) {
        showGoalLimit();
      } else {
        notify(
          i18n.t("growth.dashboard.saveGoalFailed"),
          i18n.t("growth.dashboard.tryAgain"),
        );
      }
    } finally {
      setSavingGoal(false);
    }
  }

  /** Run an overflow-menu choice. The menu closes first, then the action. */
  function runGoalAction(goal: SocialGoal, action: GoalAction) {
    setGoalMenu(null);
    switch (action) {
      case "complete":
        void setGoalDone(goal, true);
        return;
      case "reopen":
        void setGoalDone(goal, false);
        return;
      case "edit":
        openGoalSheet({ mode: "edit", goal });
        return;
      case "changeDue":
        openGoalSheet({ mode: "edit", goal, focusDue: true });
        return;
      case "archive":
        void archiveGoal(goal);
        return;
      case "restore":
        void restoreGoal(goal);
        return;
      case "delete":
        void removeGoal(goal);
    }
  }

  const load = useCallback(
    async (resetAt: string | null) => {
      setRange(7);
      setDetail(null);

      // Paint what this process already fetched, if anything, and let the
      // refresh below replace it in place. `/dashboard` is a route, so the
      // screen is rebuilt from nothing on every open — without this, every
      // open spends the full three-request wait behind one spinner.
      const cached = readDashboardCache(userId, 7, resetAt);
      if (cached) {
        setProgress(cached.progress);
        setGoals(cached.goals);
        setQuota(cached.quota);
      }
      // Only the first open of a session has nothing to show. Spinning while
      // holding a previous answer is the behaviour being removed.
      setLoading(!cached);

      // A prefetch started by pressing the Dashboard row is very likely still
      // on the wire — press-in only leads this by the length of a tap and a
      // navigation animation. Join it rather than firing a second identical
      // burst; if it lands with what this open needs, it IS this open's load.
      const prefetched = takeDashboardPrefetch();
      if (prefetched) {
        await prefetched;
        const fresh = readDashboardCache(userId, 7, resetAt);
        if (fresh) {
          setProgress(fresh.progress);
          setGoals(fresh.goals);
          setQuota(fresh.quota);
          setLoading(false);
          return;
        }
      }

      try {
        // The reflection list is not fetched here — it pages itself, keyed off
        // `reflectionsVersion`.
        const [nextProgress, nextGoals, nextQuota] = await Promise.all([
          metricsApi.progress(7, resetAt),
          goalsApi.list(),
          reflectionsApi.quota(),
        ]);
        setProgress(nextProgress);
        setGoals(nextGoals);
        setQuota(nextQuota);
        writeDashboardCache(userId, 7, resetAt, {
          progress: nextProgress,
          goals: nextGoals,
          quota: nextQuota,
        });
      } catch {
        // Every caller is `void load(...)`, so a throw here used to become an
        // unhandled rejection — and, worse, an indefinite spinner: `loading`
        // went false in the `finally` while `progress` stayed null, which the
        // old gate read as "still arriving" with nothing on the wire.
        //
        // Swallowed here but NOT hidden: `progress` staying null is what the
        // render gate reports, with a retry. A refresh that fails after a goal
        // edit keeps the previous `progress`, so a working dashboard is never
        // replaced by an error.
      } finally {
        setLoading(false);
      }
    },
    [userId],
  );

  // After logging, refresh every progress surface that can change.
  const refreshAfterLog = useCallback(async () => {
    try {
      const [nextProgress, nextQuota] = await Promise.all([
        metricsApi.progress(range, metricsResetAt),
        reflectionsApi.quota(),
      ]);
      setProgress(nextProgress);
      setQuota(nextQuota);
      setReflectionsVersion((v) => v + 1);
      // A fresh reflection clears tonight's reminder.
      void syncReflectionReminder();
    } catch {
      // Non-critical.
    }
  }, [metricsResetAt, range]);

  /**
   * Android back, and the iOS back-swipe, step OUT of a detail view before
   * they leave the dashboard.
   *
   * This is what the removed `<Modal onRequestClose>` used to do, and it was
   * not a plain dismiss — with a detail open, back returns to the overview.
   * Losing that would have made back skip a level, which is the kind of
   * regression a presentation change should never smuggle in. Registered on
   * the ROUTE, so it also covers the iOS edge-swipe the Modal never saw.
   */
  const navigation = useNavigation();
  useEffect(() => {
    if (!detail) return;
    const unsubscribe = navigation.addListener(
      // Typed loosely because the listener name is a navigation-core event
      // that expo-router does not re-export a type for.
      "beforeRemove" as never,
      ((event: { preventDefault: () => void }) => {
        event.preventDefault();
        setDetail(null);
      }) as never,
    );
    return unsubscribe;
  }, [navigation, detail]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    void (async () => {
      try {
        const controls = await readDashboardControls();
        if (cancelled) return;
        setMetricsResetAt(controls.resetAt);
        await load(controls.resetAt);
      } catch {
        // `load` reports its own failure; this catch is for the storage read
        // above it, so neither path can reject into an unhandled promise.
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, load]);

  useEffect(
    () => () => {
      if (undoGoalTimer.current) clearTimeout(undoGoalTimer.current);
    },
    [],
  );

  // "Change due date" opens the composer straight onto the date picker, so the
  // user isn't asked to find it themselves.
  useEffect(() => {
    if (goalSheet?.mode === "edit" && goalSheet.focusDue) {
      dueRef.current?.open();
    }
  }, [goalSheet]);

  // Keep each goal's two-days-before due-date reminder in sync with the current
  // goals and notification preferences (add/complete/remove/reload all land here
  // via the `goals` state; muting goal reminders cancels them).
  useEffect(() => {
    void syncGoalReminders(goals, notificationPreferences);
  }, [goals, notificationPreferences]);

  function renderGoal(goal: SocialGoal) {
    const archived = goal.archived;
    const overdue = isGoalOverdue(goal);
    const noteCount = goal.notes?.length ?? 0;

    return (
      <View
        key={goal.id}
        style={[
          styles.goalRow,
          { backgroundColor: c.surfaceAlt, borderColor: c.border },
          archived && styles.goalRowArchived,
        ]}
      >
        {/* Completion circle. Its own target, so ticking a goal off never
            collides with tapping the row to open it. */}
        {archived ? (
          <View style={styles.goalCircle}>
            <Ionicons name="archive-outline" size={22} color={c.textMuted} />
          </View>
        ) : (
          <Pressable
            accessibilityRole="checkbox"
            accessibilityState={{ checked: goal.done }}
            accessibilityLabel={i18n.t(
              goal.done
                ? "growth.dashboard.reopenGoalA11y"
                : "growth.dashboard.completeGoalA11y",
              { goal: goal.text },
            )}
            onPress={() => toggleGoal(goal)}
            hitSlop={8}
            style={styles.goalCircle}
          >
            <Ionicons
              name={goal.done ? "checkmark-circle" : "ellipse-outline"}
              size={22}
              color={goal.done ? c.success : c.textMuted}
            />
          </Pressable>
        )}

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={i18n.t("growth.dashboard.openGoalA11y", {
            goal: goal.text,
          })}
          // Archived goals are review-only; the API rejects edits to them.
          disabled={archived}
          onPress={() => openGoalSheet({ mode: "edit", goal })}
          style={styles.goalCopy}
        >
          <ThemedText
            variant="body"
            tone={goal.done || archived ? "muted" : "default"}
            style={[styles.goalText, goal.done ? styles.goalDone : null]}
          >
            {goal.text}
          </ThemedText>
          {goal.dueDate ? (
            <ThemedText
              variant="caption"
              style={{ color: overdue ? c.danger : c.textMuted }}
            >
              {overdue
                ? i18n.t("growth.dashboard.pastDue", {
                    date: formatGoalDueDate(goal.dueDate),
                  })
                : i18n.t("growth.dashboard.dueDateLabel", {
                    date: formatGoalDueDate(goal.dueDate),
                  })}
            </ThemedText>
          ) : null}
          {noteCount > 0 ? (
            <ThemedText variant="caption" tone="muted">
              {i18n.t("growth.dashboard.notesCount", { count: noteCount })}
            </ThemedText>
          ) : null}
          {archived ? (
            <ThemedText variant="caption" tone="muted">
              {i18n.t("growth.dashboard.archived")}
            </ThemedText>
          ) : null}
        </Pressable>

        {/* One overflow menu instead of a permanent row of icons — in
            particular, no delete button sitting a stray tap away. */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={i18n.t("growth.dashboard.goalMenuA11y", {
            goal: goal.text,
          })}
          onPress={() => setGoalMenu(goal)}
          hitSlop={10}
          style={styles.goalIconButton}
        >
          <Ionicons name="ellipsis-horizontal" size={18} color={c.textMuted} />
        </Pressable>
      </View>
    );
  }

  if (!visible) return null;

  return (
    /*
      A FRAGMENT, not a native modal, and that is the whole fix.
      `/dashboard` is already declared as a modal ROUTE in
      `app/_layout.tsx`, so wrapping its body in a second RN
      modal presented the dashboard twice: the route's own,
      then the inner slide. On iOS every RN modal is a real
      `UIViewController` presentation, which is why opening and
      CLOSING both dragged there and much less on Android, where
      a `Modal` is only a `Dialog`. Closing was the tell — it
      fetches nothing, so nothing but the presentation could have
      been costing it.

      The route owns the slide now. `onRequestClose` used to be
      the Android back button, and it did NOT simply dismiss: it
      stepped out of a detail view first. That behaviour moved to
      the `beforeRemove` effect above, which also covers the iOS
      back-swipe the Modal never saw.
    */
    <>
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={closeCurrentViewA11y}
          style={styles.backdrop}
          onPress={closeCurrentView}
        />

        <KeyboardAvoidingContainer style={styles.sheetWrap}>
          <View
            style={[
              styles.sheet,
              { backgroundColor: c.background, borderColor: c.border },
            ]}
          >
            <View style={styles.grabberWrap}>
              <View style={[styles.grabber, { backgroundColor: c.border }]} />
            </View>

            <View style={styles.header}>
              <ThemedText variant="title" numberOfLines={2} style={styles.headerTitle}>
                {range === 30
                  ? i18n.t("growth.dashboard.monthTitle")
                  : i18n.t("growth.dashboard.weekTitle")}
              </ThemedText>
              <Pressable
                onPress={closeCurrentView}
                hitSlop={10}
                style={styles.headerClose}
                accessibilityLabel={closeCurrentViewA11y}
              >
                <Ionicons name="close" size={24} color={c.textSecondary} />
              </Pressable>
            </View>

            {loading ? (
              <View style={styles.loading}>
                <ActivityIndicator color={c.tint} />
              </View>
            ) : !progress ? (
              /* Not loading and nothing to show means the load failed. This
                 branch used to be the spinner above, which never stopped. */
              <View style={styles.loading}>
                <ThemedText
                  variant="body"
                  tone="secondary"
                  style={styles.loadFailedCopy}
                >
                  {i18n.t("growth.dashboard.loadFailed")}
                </ThemedText>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void load(metricsResetAt)}
                  style={({ pressed }) => [
                    styles.loadRetry,
                    { borderColor: c.border, backgroundColor: c.surfaceAlt },
                    pressed && styles.loadRetryPressed,
                  ]}
                >
                  <ThemedText variant="bodyStrong" tone="tint">
                    {i18n.t("growth.dashboard.retry")}
                  </ThemedText>
                </Pressable>
              </View>
            ) : (
              <ScrollView
                showsVerticalScrollIndicator={false}
                keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "on-drag"}
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={styles.content}
              >
              {Platform.OS === "web" ? (
                <ThemedText
                  variant="body"
                  tone="secondary"
                  style={styles.encourage}
                >
                  {i18n.t("growth.dashboard.encouragement")}
                </ThemedText>
              ) : null}

              <View style={[styles.section, styles.firstSection]}>
                <View style={styles.summaryHeader}>
                  <SectionLabel
                    text={detail ? "Progress detail" : "Overview"}
                    style={styles.flexShrinkLabel}
                  />
                  <View style={styles.rangeToggle}>
                    {([7, 30] as const).map((r) => {
                      const active = range === r;
                      return (
                        <Pressable
                          key={r}
                          accessibilityRole="button"
                          accessibilityState={{ selected: active }}
                          onPress={() => void changeRange(r)}
                          style={[
                            styles.rangePill,
                            {
                              backgroundColor: active ? c.tint : c.surfaceAlt,
                              borderColor: active ? c.tint : c.border,
                            },
                          ]}
                        >
                          <ThemedText
                            variant="caption"
                            tone={active ? "onTint" : "secondary"}
                            numberOfLines={1}
                            maxFontSizeMultiplier={1.3}
                          >
                            {r === 7
                              ? i18n.t("growth.dashboard.week")
                              : i18n.t("growth.dashboard.month")}
                          </ThemedText>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
                {detail === null ? (
                  <ThemedText variant="caption" tone="muted">
                    {formatProgressRange(progress.rangeStart, progress.rangeEnd)}
                  </ThemedText>
                ) : null}
                {detail ? (
                  <>
                    <ProgressDetailHeader detail={detail} />
                    {/* Keyed on the range so switching Week/Month starts these
                        lists back at page 1 rather than stranding the reader on
                        a page that only existed in the other window. */}
                    {detail === "practice" ? (
                      <PracticeProgressDetail
                        key={range}
                        data={progress.practice}
                      />
                    ) : detail === "goals" ? (
                      <GoalsProgressDetail
                        key={range}
                        data={progress.goals}
                        rangeDays={range}
                      />
                    ) : (
                      <ReflectionsProgressDetail data={progress.reflections} />
                    )}
                  </>
                ) : (
                  <ProgressStoryGrid
                    data={progress}
                    onSelect={(nextDetail) => {
                      setDetail(nextDetail);
                      if (nextDetail === "goals") setGoalsOpen(true);
                    }}
                  />
                )}
              </View>

              {detail === null ? (
                <View style={styles.section}>
                {isPlus ? (
                  <>
                    <SectionLabel text={i18n.t("growth.dashboard.trends")} />
                    {progress.trends.points.length > 0 ? (
                      <TrendChart
                        points={progress.trends.points}
                        unit={progress.trends.unit}
                      />
                    ) : (
                      <ThemedText variant="caption" tone="muted">
                        {i18n.t("growth.dashboard.trendEmpty")}
                      </ThemedText>
                    )}
                  </>
                ) : (
                  <Pressable
                    onPress={() => openPlusGate("monthly")}
                    style={[
                      styles.upsell,
                      { backgroundColor: c.surfaceAlt, borderColor: c.border },
                    ]}
                  >
                    <Ionicons name="trending-up" size={20} color={c.tint} />
                    <View style={styles.upsellCopy}>
                      <ThemedText variant="bodyStrong">
                        {i18n.t("growth.dashboard.monthlyTitle")}
                      </ThemedText>
                      <ThemedText variant="caption" tone="secondary">
                        {i18n.t("growth.dashboard.monthlyBody")}
                      </ThemedText>
                    </View>
                    <Ionicons
                      name="chevron-forward"
                      size={18}
                      color={c.textMuted}
                    />
                  </Pressable>
                )}
                <View style={styles.summaryControlFooter}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={i18n.t(
                      "growth.dashboard.controlsTitle",
                    )}
                    onPress={() => setDashboardControlsOpen(true)}
                    style={({ pressed }) => [
                      styles.dashboardControlButton,
                      {
                        backgroundColor: c.surfaceAlt,
                        borderColor: c.border,
                      },
                      pressed && styles.dashboardControlButtonPressed,
                    ]}
                  >
                    <Ionicons
                      name="ellipsis-horizontal"
                      size={18}
                      color={c.textSecondary}
                    />
                  </Pressable>
                </View>
                </View>
              ) : null}

              {detail === "goals" ? (
                <View style={styles.section}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ expanded: goalsOpen }}
                  onPress={() => setGoalsOpen((open) => !open)}
                  style={[
                    styles.goalsHeader,
                    styles.sectionActionHeader,
                    sectionActionHeaderColors,
                  ]}
                >
                  <View style={styles.goalsHeaderCopy}>
                    <SectionLabel
                      text={i18n.t("growth.dashboard.yourSocialGoals")}
                    />
                    <ThemedText variant="caption" tone="muted">
                      {i18n.t("growth.dashboard.activeCount", {
                        active: activeGoals.length,
                        limit: goalLimit,
                      })}
                    </ThemedText>
                  </View>
                  <Ionicons
                    name={goalsOpen ? "chevron-down" : "chevron-forward"}
                    size={18}
                    color={c.textMuted}
                  />
                </Pressable>

                {undoGoal ? (
                  <View
                    style={[
                      styles.undoRow,
                      {
                        backgroundColor: c.successTint,
                        borderColor: c.success,
                      },
                    ]}
                  >
                    <ThemedText
                      variant="caption"
                      tone="success"
                      style={styles.undoCopy}
                      numberOfLines={2}
                    >
                      {i18n.t("growth.dashboard.goalMovedToCompleted", {
                        title: undoGoal.text,
                      })}
                    </ThemedText>
                    <Pressable
                      accessibilityRole="button"
                      onPress={() => void undoCompletedGoal()}
                      hitSlop={8}
                    >
                      <ThemedText variant="bodyStrong" tone="success">
                        {i18n.t("growth.dashboard.undo")}
                      </ThemedText>
                    </Pressable>
                  </View>
                ) : null}

                {goalsOpen ? (
                  <>
                    {/* Creation lives behind one clear button at the top,
                        rather than an input hanging off the end of the list. */}
                    <Pressable
                      accessibilityRole="button"
                      onPress={() =>
                        atGoalLimit ? showGoalLimit() : openGoalSheet({ mode: "create" })
                      }
                      style={({ pressed }) => [
                        styles.newGoalButton,
                        {
                          borderColor: atGoalLimit ? c.border : c.tint,
                          backgroundColor: atGoalLimit
                            ? c.surfaceAlt
                            : c.backgroundSelected,
                          opacity: pressed ? 0.78 : 1,
                        },
                      ]}
                    >
                      <Ionicons
                        name="add"
                        size={18}
                        color={atGoalLimit ? c.textMuted : c.tint}
                      />
                      <ThemedText
                        variant="bodyStrong"
                        tone={atGoalLimit ? "muted" : "tint"}
                      >
                        {i18n.t("growth.dashboard.newGoal")}
                      </ThemedText>
                    </Pressable>
                    {atGoalLimit ? (
                      <ThemedText variant="caption" tone="muted">
                        {plan !== "plus"
                          ? i18n.t("growth.dashboard.plusGate.goalsBody")
                          : goalSlotsFullCopy(activeGoals.length)}
                      </ThemedText>
                    ) : null}

                    <View style={styles.goalTabs}>
                      {(
                        [
                          [
                            "active",
                            i18n.t("growth.dashboard.tabActive", {
                              count: activeGoals.length,
                            }),
                          ],
                          [
                            "completed",
                            i18n.t("growth.dashboard.tabCompleted", {
                              count: completedGoals.length,
                            }),
                          ],
                          [
                            "archived",
                            i18n.t("growth.dashboard.tabArchived", {
                              count: archivedGoals.length,
                            }),
                          ],
                        ] as const
                      ).map(([value, label], index) => {
                        const active = goalTab === value;
                        return (
                          <Fragment key={value}>
                            {index > 0 ? (
                              <ThemedText
                                variant="caption"
                                tone="muted"
                                accessibilityElementsHidden
                                importantForAccessibility="no"
                                style={styles.goalTabSeparator}
                              >
                                |
                              </ThemedText>
                            ) : null}
                            <Pressable
                              accessibilityRole="button"
                              accessibilityState={{ selected: active }}
                              onPress={() => showGoalTab(value)}
                              hitSlop={6}
                              style={({ pressed }) => [
                                styles.goalTab,
                                pressed && styles.goalTabPressed,
                              ]}
                            >
                              <ThemedText
                                variant="caption"
                                tone={active ? "tint" : "secondary"}
                                numberOfLines={2}
                                maxFontSizeMultiplier={1.3}
                                style={[
                                  styles.goalTabText,
                                  active && styles.goalTabTextActive,
                                ]}
                              >
                                {label}
                              </ThemedText>
                            </Pressable>
                          </Fragment>
                        );
                      })}
                    </View>

                    {visibleGoals.length > 0 ? (
                      <>
                        {visibleGoals.map(renderGoal)}
                        <Pager
                          page={goalPage}
                          totalPages={goalPages}
                          onChange={setGoalPage}
                        />
                      </>
                    ) : (
                      <View
                        style={[
                          styles.emptyGoals,
                          {
                            backgroundColor: c.surfaceAlt,
                            borderColor: c.border,
                          },
                        ]}
                      >
                        <ThemedText variant="caption" tone="muted" center>
                          {emptyGoalsCopy}
                        </ThemedText>
                      </View>
                    )}
                  </>
                ) : null}
                </View>
              ) : null}

              {detail === "reflections" ? (
                <View style={styles.section}>
                  <View style={styles.goalsHeaderCopy}>
                    <SectionLabel
                      text={i18n.t("growth.dashboard.yourReflections")}
                    />
                    {quota ? (
                      <ThemedText variant="caption" tone="muted">
                        {/* `limit === null` means unlimited (Plus). */}
                        {quota.limit === null
                          ? i18n.t("growth.dashboard.quotaSummaryUnlimited")
                          : i18n.t("growth.dashboard.quotaSummary", {
                              used: quota.used,
                              limit: quota.limit,
                            })}
                      </ThemedText>
                    ) : null}
                  </View>

                  <ReflectionHistoryList
                    refreshKey={reflectionsVersion}
                    onOpen={setSelectedReflection}
                  />

                  {quota ? (
                    <ThemedText
                      variant="caption"
                      tone="secondary"
                      style={styles.quotaIntro}
                    >
                      {reflectionQuotaIntro(quota.plan)}
                    </ThemedText>
                  ) : null}

                  <Pressable
                    onPress={() => setReflectOpen(true)}
                    style={[
                      styles.reflectBtn,
                      { borderColor: c.border, backgroundColor: c.surface },
                    ]}
                  >
                    <Ionicons
                      name="add-circle-outline"
                      size={18}
                      color={c.tint}
                    />
                    <ThemedText variant="bodyStrong" tone="tint">
                      {i18n.t("growth.dashboard.addReflection")}
                    </ThemedText>
                  </Pressable>
                </View>
              ) : null}

              {detail === "reflections" ? (
                <View style={styles.section}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ expanded: reflectionRemindersOpen }}
                  onPress={() => setReflectionRemindersOpen((open) => !open)}
                  style={[
                    styles.goalsHeader,
                    styles.sectionActionHeader,
                    sectionActionHeaderColors,
                  ]}
                >
                  <View style={styles.goalsHeaderCopy}>
                    <SectionLabel
                      text={i18n.t("growth.dashboard.reflectionReminders")}
                    />
                    <ThemedText variant="caption" tone="secondary">
                      {i18n.t("growth.dashboard.nudgeBody")}
                    </ThemedText>
                  </View>
                  <Ionicons
                    name={
                      reflectionRemindersOpen
                        ? "chevron-down"
                        : "chevron-forward"
                    }
                    size={18}
                    color={c.textMuted}
                  />
                </Pressable>

                {reflectionRemindersOpen ? (
                  <>
                    <View style={styles.freqRow}>
                      {FREQUENCY_OPTIONS.map((option) => {
                        const active = reflectionFrequency === option.value;
                        return (
                          <Pressable
                            key={option.value}
                            accessibilityRole="button"
                            accessibilityState={{ selected: active }}
                            onPress={() => changeFrequency(option.value)}
                            style={[
                              styles.freqChip,
                              {
                                borderColor: active ? c.tint : c.border,
                                backgroundColor: active
                                  ? c.backgroundSelected
                                  : c.surfaceAlt,
                              },
                            ]}
                          >
                            <ThemedText
                              variant="caption"
                              tone={active ? "tint" : "secondary"}
                              numberOfLines={2}
                              maxFontSizeMultiplier={1.3}
                              style={styles.freqChipText}
                            >
                              {option.label}
                            </ThemedText>
                          </Pressable>
                        );
                      })}
                    </View>

                    {reflectionFrequency === "custom" ? (
                      <View style={styles.customReminder}>
                        <ThemedText variant="caption" tone="secondary">
                          {i18n.t("growth.dashboard.customDaysLabel")}
                        </ThemedText>
                        <View style={styles.freqRow}>
                          {CUSTOM_WEEKDAYS.map((day) => {
                            const active = customDays.includes(day.value);
                            return (
                              <Pressable
                                key={day.value}
                                accessibilityRole="button"
                                accessibilityState={{ selected: active }}
                                onPress={() => toggleCustomDay(day.value)}
                                style={[
                                  styles.dayChip,
                                  {
                                    borderColor: active ? c.tint : c.border,
                                    backgroundColor: active
                                      ? c.backgroundSelected
                                      : c.surfaceAlt,
                                  },
                                ]}
                              >
                                <ThemedText
                                  variant="caption"
                                  tone={active ? "tint" : "secondary"}
                                  maxFontSizeMultiplier={1.3}
                                >
                                  {day.label}
                                </ThemedText>
                              </Pressable>
                            );
                          })}
                        </View>
                        <ThemedText variant="caption" tone="secondary">
                          {i18n.t("growth.dashboard.customTimeLabel")}
                        </ThemedText>
                        <ReminderTimePicker
                          value={customTime}
                          onChange={changeCustomTime}
                        />
                      </View>
                    ) : null}

                    <View
                      style={[
                        styles.emailRow,
                        {
                          backgroundColor: c.surfaceAlt,
                          borderColor: c.border,
                        },
                      ]}
                    >
                      <View style={styles.emailCopy}>
                        <ThemedText variant="bodyStrong">
                          {i18n.t("growth.dashboard.weeklyEmail")}
                        </ThemedText>
                        <ThemedText variant="caption" tone="secondary">
                          {i18n.t("growth.dashboard.weeklyEmailBody")}
                        </ThemedText>
                      </View>
                      <Switch
                        value={weeklyEmailOptIn}
                        onValueChange={toggleWeeklyEmail}
                        trackColor={{ true: c.tint, false: c.border }}
                      />
                    </View>
                  </>
                ) : null}
                </View>
              ) : null}

              {detail === null ? (
                <>
                  <SectionLabel
                    text={i18n.t("growth.dashboard.shareProgress")}
                  />
                  <ShareProgressCard
                    summary={progress}
                    plan={plan}
                    onUpgrade={() => openPlusGate("poster")}
                  />

                  <SectionLabel
                    text={i18n.t("growth.dashboard.vibeCard")}
                    style={styles.vibeCardSectionLabel}
                  />
                  <ProfileVibeCardEntry replaceOnNavigate />

                  <SectionLabel
                    text={i18n.t("growth.dashboard.progressData")}
                  />
              {isPlus ? (
                exportOptionsOpen ? (
                  <View
                    style={[
                      styles.exportPanel,
                      { backgroundColor: c.surfaceAlt, borderColor: c.border },
                    ]}
                  >
                    <View style={styles.exportPanelHeader}>
                      <View style={styles.upsellCopy}>
                        <ThemedText variant="bodyStrong">
                          {i18n.t("growth.dashboard.exportTitle")}
                        </ThemedText>
                        <ThemedText variant="caption" tone="secondary">
                          {i18n.t("growth.dashboard.exportBody")}
                        </ThemedText>
                      </View>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={i18n.t(
                          "growth.dashboard.closeExportA11y",
                        )}
                        onPress={() => setExportOptionsOpen(false)}
                        hitSlop={10}
                        style={({ pressed }) => [
                          styles.exportCloseButton,
                          { backgroundColor: c.surface, borderColor: c.border },
                          pressed && styles.exportButtonPressed,
                        ]}
                      >
                        <Ionicons
                          name="close"
                          size={18}
                          color={c.textSecondary}
                        />
                      </Pressable>
                    </View>

                    <View style={styles.exportIntro}>
                      <Ionicons
                        name="shield-checkmark-outline"
                        size={20}
                        color={c.tint}
                      />
                      <View style={styles.upsellCopy}>
                        <ThemedText variant="bodyStrong">
                          {i18n.t("growth.dashboard.fileHelpTitle")}
                        </ThemedText>
                        <ThemedText variant="caption" tone="secondary">
                          {i18n.t("growth.dashboard.fileHelpBody")}
                        </ThemedText>
                      </View>
                    </View>

                    <View style={styles.exportGroup}>
                      <ThemedText variant="caption" tone="secondary">
                        {i18n.t("growth.dashboard.chooseFormat")}
                      </ThemedText>
                      <View style={styles.exportChipRow}>
                        {EXPORT_FORMATS.map((option) => {
                          const active = exportFormat === option.value;
                          return (
                            <Pressable
                              key={option.value}
                              accessibilityRole="button"
                              accessibilityState={{
                                selected: active,
                                disabled: option.disabled,
                              }}
                              disabled={option.disabled}
                              onPress={() => setExportFormat(option.value)}
                              style={[
                                styles.exportChip,
                                {
                                  borderColor: active ? c.tint : c.border,
                                  backgroundColor: active
                                    ? c.backgroundSelected
                                    : c.surface,
                                  opacity: option.disabled ? 0.55 : 1,
                                },
                              ]}
                            >
                              <ThemedText
                                variant="caption"
                                tone={active ? "tint" : "secondary"}
                                numberOfLines={2}
                                maxFontSizeMultiplier={1.3}
                                style={styles.exportChipText}
                              >
                                {option.label}
                              </ThemedText>
                            </Pressable>
                          );
                        })}
                      </View>
                    </View>

                    <View style={styles.exportGroup}>
                      <ThemedText variant="caption" tone="secondary">
                        {i18n.t("growth.dashboard.include")}
                      </ThemedText>
                      {(
                        [
                          [
                            "growthStats",
                            i18n.t(
                              "growth.dashboard.exportSections.growthStats",
                            ),
                          ],
                          [
                            "weeklySummaries",
                            i18n.t(
                              "growth.dashboard.exportSections.weeklySummaries",
                            ),
                          ],
                          [
                            "reflections",
                            i18n.t(
                              "growth.dashboard.exportSections.reflections",
                            ),
                          ],
                          [
                            "privateNotes",
                            i18n.t(
                              "growth.dashboard.exportSections.privateNotes",
                            ),
                          ],
                        ] as const
                      ).map(([key, label]) => {
                        const checked = exportSections[key];
                        return (
                          <Pressable
                            key={key}
                            accessibilityRole="checkbox"
                            accessibilityState={{ checked }}
                            onPress={() => toggleExportSection(key)}
                            style={styles.exportCheckRow}
                          >
                            <Ionicons
                              name={checked ? "checkbox" : "square-outline"}
                              size={20}
                              color={checked ? c.tint : c.textMuted}
                            />
                            <ThemedText variant="body">{label}</ThemedText>
                          </Pressable>
                        );
                      })}
                    </View>

                    <View style={styles.exportGroup}>
                      <ThemedText variant="caption" tone="secondary">
                        {i18n.t("growth.dashboard.dateRange")}
                      </ThemedText>
                      <View style={styles.exportChipRow}>
                        {EXPORT_RANGES.map((option) => {
                          const active = exportRange === option.value;
                          return (
                            <Pressable
                              key={option.value}
                              accessibilityRole="button"
                              accessibilityState={{ selected: active }}
                              onPress={() => setExportRange(option.value)}
                              style={[
                                styles.exportChip,
                                {
                                  borderColor: active ? c.tint : c.border,
                                  backgroundColor: active
                                    ? c.backgroundSelected
                                    : c.surface,
                                },
                              ]}
                            >
                              <ThemedText
                                variant="caption"
                                tone={active ? "tint" : "secondary"}
                                numberOfLines={2}
                                maxFontSizeMultiplier={1.3}
                                style={styles.exportChipText}
                              >
                                {option.label}
                              </ThemedText>
                            </Pressable>
                          );
                        })}
                      </View>
                    </View>

                    <Pressable
                      onPress={handleExport}
                      disabled={exporting}
                      style={({ pressed }) => [
                        styles.exportButton,
                        { backgroundColor: c.tint },
                        (pressed || exporting) && styles.exportButtonPressed,
                      ]}
                    >
                      {exporting ? (
                        <ActivityIndicator color={c.onTint} />
                      ) : (
                        <Ionicons
                          name="download-outline"
                          size={19}
                          color={c.onTint}
                        />
                      )}
                      <ThemedText variant="bodyStrong" tone="onTint">
                        {exporting
                          ? i18n.t("growth.dashboard.preparing")
                          : i18n.t("growth.dashboard.export")}
                      </ThemedText>
                    </Pressable>
                  </View>
                ) : (
                  <Pressable
                    onPress={() => setExportOptionsOpen(true)}
                    style={[
                      styles.dataRow,
                      { backgroundColor: c.surfaceAlt, borderColor: c.border },
                    ]}
                  >
                    <Ionicons
                      name="download-outline"
                      size={20}
                      color={c.tint}
                    />
                    <View style={styles.upsellCopy}>
                      <ThemedText variant="bodyStrong">
                        {i18n.t("growth.dashboard.exportTitle")}
                      </ThemedText>
                      <ThemedText variant="caption" tone="secondary">
                        {i18n.t("growth.dashboard.exportBody")}
                      </ThemedText>
                    </View>
                    <Ionicons
                      name="chevron-forward"
                      size={18}
                      color={c.textMuted}
                    />
                  </Pressable>
                )
              ) : (
                <Pressable
                  onPress={() => openPlusGate("export")}
                  style={[
                    styles.dataRow,
                    { backgroundColor: c.surfaceAlt, borderColor: c.border },
                  ]}
                >
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color={c.textMuted}
                  />
                  <View style={styles.upsellCopy}>
                    <ThemedText variant="bodyStrong">
                      {i18n.t("growth.dashboard.exportTitle")}
                    </ThemedText>
                    <ThemedText variant="caption" tone="secondary">
                      {i18n.t("growth.dashboard.exportPlusBody")}
                    </ThemedText>
                  </View>
                  <Ionicons
                    name="chevron-forward"
                    size={18}
                    color={c.textMuted}
                  />
                </Pressable>
              )}
                </>
              ) : null}
              </ScrollView>
            )}
          </View>
        </KeyboardAvoidingContainer>
      </View>

      {/* Goal composer: "+ New goal", and the details view a row opens. */}
      <Modal
        visible={goalSheet !== null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={closeGoalSheet}
      >
        <View style={styles.goalEditScreen}>
          <BlurredBackdrop
            accessibilityLabel={i18n.t("growth.dashboard.closeGoalA11y")}
            style={styles.goalEditBackdrop}
            onPress={closeGoalSheet}
          />
          <View
            style={[
              styles.goalEditCard,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <View style={styles.goalEditHeader}>
              <View style={styles.upsellCopy}>
                <ThemedText variant="bodyStrong">
                  {i18n.t(
                    goalSheet?.mode === "create"
                      ? "growth.dashboard.newGoal"
                      : "growth.dashboard.editGoal",
                  )}
                </ThemedText>
                <ThemedText variant="caption" tone="secondary">
                  {i18n.t("growth.dashboard.goalPrompt")}
                </ThemedText>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={i18n.t("growth.dashboard.closeGoalA11y")}
                onPress={closeGoalSheet}
                hitSlop={10}
                style={styles.goalIconButton}
              >
                <Ionicons name="close" size={20} color={c.textSecondary} />
              </Pressable>
            </View>

            <KeyboardScrollView
              style={styles.goalEditScroll}
              contentContainerStyle={styles.goalEditScrollContent}
            >
              <TextInput
                value={goalTitleDraft}
                onChangeText={(value) => {
                  // One line of text, so newlines are folded away rather than
                  // stored and rendered as a gap in the goal list.
                  const next = value
                    .replace(/[\r\n]+/g, " ")
                    .slice(0, GOAL_TEXT_MAX_LENGTH);
                  setGoalTitleDraft(next);
                  if (next.trim()) setGoalTitleMissing(false);
                }}
                maxLength={GOAL_TEXT_MAX_LENGTH}
                multiline
                textAlignVertical="top"
                placeholder={i18n.t("growth.dashboard.goalPlaceholder")}
                placeholderTextColor={c.textMuted}
                style={[
                  styles.goalTitleInput,
                  {
                    color: c.text,
                    borderColor: goalTitleMissing ? c.danger : c.border,
                    backgroundColor: c.surfaceAlt,
                  },
                ]}
              />
              {goalTitleMissing ? (
                <ThemedText variant="caption" style={{ color: c.danger }}>
                  {i18n.t("growth.dashboard.goalTextRequired")}
                </ThemedText>
              ) : null}

              <View style={styles.goalFieldGroup}>
                <ThemedText variant="label" tone="muted">
                  {i18n.t("growth.dashboard.dueDateOptional")}
                </ThemedText>
                <GoalDuePicker
                  ref={dueRef}
                  value={goalDueDraft}
                  onChange={setGoalDueDraft}
                />
              </View>

              <View style={styles.goalFieldGroup}>
                <ThemedText variant="label" tone="muted">
                  {i18n.t("growth.dashboard.notesOptional")}
                </ThemedText>
                <View style={styles.goalNotesList}>
                  {goalNoteDrafts.map((note, index) => (
                    <View key={index} style={styles.goalNoteRow}>
                      <View style={styles.goalNoteField}>
                        <TextInput
                          value={note}
                          onChangeText={(value) => updateGoalNote(index, value)}
                          maxLength={GOAL_NOTE_MAX_LENGTH}
                          // Wraps instead of scrolling sideways, so a note near
                          // the limit is readable in full without dragging the
                          // caret.
                          multiline
                          // Android otherwise centres the first line in the
                          // grown box.
                          textAlignVertical="top"
                          placeholder={i18n.t(
                            "growth.dashboard.notePlaceholder",
                            { count: index + 1 },
                          )}
                          placeholderTextColor={c.textMuted}
                          style={[
                            styles.goalNoteInput,
                            {
                              color: c.text,
                              borderColor: c.border,
                              backgroundColor: c.surfaceAlt,
                            },
                          ]}
                        />
                        <ThemedText
                          variant="caption"
                          tone={
                            note.length >= GOAL_NOTE_MAX_LENGTH
                              ? "tint"
                              : "muted"
                          }
                          style={styles.goalNoteCount}
                        >
                          {note.length}/{GOAL_NOTE_MAX_LENGTH}
                        </ThemedText>
                      </View>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={i18n.t(
                          "growth.dashboard.removeNoteA11y",
                          { count: index + 1 },
                        )}
                        onPress={() => removeGoalNoteInput(index)}
                        hitSlop={8}
                        style={styles.goalIconButton}
                      >
                        <Ionicons name="close" size={17} color={c.textMuted} />
                      </Pressable>
                    </View>
                  ))}
                </View>

                <View style={styles.goalEditFooter}>
                  <Pressable
                    accessibilityRole="button"
                    onPress={addGoalNoteInput}
                    disabled={goalNoteDrafts.length >= GOAL_NOTE_MAX_COUNT}
                    style={[
                      styles.goalAddNoteButton,
                      {
                        borderColor: c.border,
                        opacity:
                          goalNoteDrafts.length >= GOAL_NOTE_MAX_COUNT
                            ? 0.55
                            : 1,
                      },
                    ]}
                  >
                    <Ionicons name="add" size={17} color={c.tint} />
                    <ThemedText variant="caption" tone="tint">
                      {i18n.t("growth.dashboard.addNote")}
                    </ThemedText>
                  </Pressable>
                  <ThemedText variant="caption" tone="muted">
                    {goalNoteDrafts.length}/{GOAL_NOTE_MAX_COUNT}
                  </ThemedText>
                </View>
              </View>
            </KeyboardScrollView>

            <Pressable
              onPress={saveGoalSheet}
              disabled={savingGoal}
              style={({ pressed }) => [
                styles.goalSaveButton,
                { backgroundColor: c.tint },
                (pressed || savingGoal) && styles.exportButtonPressed,
              ]}
            >
              {savingGoal ? (
                <ActivityIndicator color={c.onTint} />
              ) : (
                <Ionicons name="checkmark" size={18} color={c.onTint} />
              )}
              <ThemedText variant="bodyStrong" tone="onTint">
                {i18n.t(
                  goalSheet?.mode === "create"
                    ? "growth.dashboard.createGoal"
                    : "growth.dashboard.saveGoal",
                )}
              </ThemedText>
            </Pressable>
          </View>
        </View>
      </Modal>

      {/* Row overflow menu. Which actions appear depends on the goal's state —
          an overdue goal is offered "Mark complete" and "Change due date". */}
      <Modal
        visible={goalMenu !== null}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setGoalMenu(null)}
      >
        <View style={styles.goalEditScreen}>
          <BlurredBackdrop
            accessibilityLabel={i18n.t("growth.dashboard.closeGoalMenuA11y")}
            style={styles.goalEditBackdrop}
            onPress={() => setGoalMenu(null)}
          />
          <View
            style={[
              styles.goalMenuCard,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            {goalMenu ? (
              <>
                {goalMenuActions(goalMenu).map((action) => {
                  const destructive = action === "delete";
                  // Shown, but refused: a completed goal is a record, and Edit
                  // sits greyed directly under the Reopen that unlocks it.
                  const disabled = isGoalActionDisabled(goalMenu, action);
                  return (
                    <Pressable
                      key={action}
                      accessibilityRole="button"
                      accessibilityState={{ disabled }}
                      disabled={disabled}
                      onPress={() => runGoalAction(goalMenu, action)}
                      style={({ pressed }) => [
                        styles.goalMenuRow,
                        { borderColor: c.border },
                        pressed && !disabled && styles.goalTabPressed,
                        disabled && styles.goalMenuRowDisabled,
                      ]}
                    >
                      <Ionicons
                        name={GOAL_ACTION_ICONS[action]}
                        size={18}
                        color={
                          disabled
                            ? c.textMuted
                            : destructive
                              ? c.danger
                              : c.textSecondary
                        }
                      />
                      <ThemedText
                        variant="body"
                        tone={
                          disabled ? "muted" : destructive ? "danger" : "default"
                        }
                      >
                        {i18n.t(`growth.dashboard.action.${action}`)}
                      </ThemedText>
                    </Pressable>
                  );
                })}
              </>
            ) : null}
          </View>
        </View>
      </Modal>

      <Modal
        visible={dashboardControlsOpen}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setDashboardControlsOpen(false)}
      >
        <View style={styles.controlScreen}>
          <BlurredBackdrop
            accessibilityLabel={i18n.t("growth.dashboard.closeControlsA11y")}
            style={styles.controlBackdrop}
            onPress={() => setDashboardControlsOpen(false)}
          />
          <View
            style={[
              styles.controlCard,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <View style={styles.controlHeader}>
              <View style={styles.upsellCopy}>
                <ThemedText variant="bodyStrong">
                  {i18n.t("growth.dashboard.controlsTitle")}
                </ThemedText>
                <ThemedText variant="caption" tone="secondary">
                  {i18n.t("growth.dashboard.controlsBody")}
                </ThemedText>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={i18n.t(
                  "growth.dashboard.closeControlsA11y",
                )}
                onPress={() => setDashboardControlsOpen(false)}
                hitSlop={10}
                style={styles.goalIconButton}
              >
                <Ionicons name="close" size={20} color={c.textSecondary} />
              </Pressable>
            </View>

            <Pressable
              accessibilityRole="button"
              onPress={() => void resetDashboardMetrics()}
              style={[
                styles.controlRow,
                { backgroundColor: c.surfaceAlt, borderColor: c.border },
              ]}
            >
              <Ionicons name="refresh-outline" size={20} color={c.tint} />
              <View style={styles.upsellCopy}>
                <ThemedText variant="bodyStrong">
                  {i18n.t("growth.dashboard.reset")}
                </ThemedText>
                <ThemedText variant="caption" tone="secondary">
                  {i18n.t("growth.dashboard.resetBody")}
                </ThemedText>
              </View>
            </Pressable>
          </View>
        </View>
      </Modal>

      <ReflectionModal
        visible={reflectOpen}
        onClose={() => setReflectOpen(false)}
        onUpgrade={() => {
          setReflectOpen(false);
          onClose();
        }}
        onLogged={refreshAfterLog}
      />

      <ReflectionDetailModal
        reflection={selectedReflection}
        onClose={() => setSelectedReflection(null)}
        onDelete={() => {
          // The counters and the list both derive from the deleted row, so
          // refresh the same surfaces a new reflection would.
          void refreshAfterLog();
        }}
      />

      {plusGateCopy ? (
        <PlusFeatureUpsell
          visible
          title={plusGateCopy.title}
          intro={plusGateCopy.intro}
          body={plusGateCopy.body}
          body2={plusGateCopy.body2}
          cta={i18n.t("growth.dashboard.plusGate.cta")}
          dismiss={i18n.t("growth.dashboard.plusGate.dismiss")}
          trigger={plusGateCopy.trigger}
          onDismiss={() => setPlusGate(null)}
          onConfirm={() => {
            setPlusGate(null);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

function SectionLabel({
  text,
  style,
}: {
  text: string;
  style?: StyleProp<TextStyle>;
}) {
  return (
    <ThemedText
      variant="label"
      tone="muted"
      numberOfLines={2}
      style={[styles.sectionLabel, style]}
    >
      {text.toUpperCase()}
    </ThemedText>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    justifyContent: "flex-end",
  },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.5)",
  },
  sheetWrap: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  sheet: {
    flex: Platform.OS === "web" ? undefined : 1,
    width: "100%",
    height: Platform.OS === "web" ? "90%" : undefined,
    marginTop: Platform.OS === "web" ? 0 : Spacing.lg + 10,
    maxHeight: Platform.OS === "web" ? "90%" : "100%",
    borderTopLeftRadius: Platform.OS === "web" ? Radius.xl : 0,
    borderTopRightRadius: Platform.OS === "web" ? Radius.xl : 0,
    borderWidth: 1,
    paddingBottom: 0,
  },
  grabberWrap: { alignItems: "center", paddingTop: Spacing.sm },
  grabber: { width: 40, height: 4, borderRadius: 2 },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.md,
    paddingHorizontal: Spacing.lg,
    paddingTop: Spacing.sm,
    paddingBottom: Spacing.md,
  },
  headerTitle: { flexShrink: 1, minWidth: 0 },
  headerClose: { flexShrink: 0 },
  loading: { paddingVertical: Spacing.xxxl, alignItems: "center", gap: Spacing.md },
  loadFailedCopy: { textAlign: "center", paddingHorizontal: Spacing.lg },
  loadRetry: {
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    borderRadius: Radius.pill,
    borderWidth: StyleSheet.hairlineWidth,
  },
  loadRetryPressed: { opacity: 0.7 },
  content: { paddingHorizontal: Spacing.lg, paddingBottom: 0, gap: Spacing.md },
  section: { gap: Spacing.md, marginBottom: 30 },
  firstSection: { marginTop: Spacing.sm },
  encourage: { marginTop: Spacing.xs },
  sectionLabel: { marginTop: Spacing.sm, letterSpacing: 1 },
  vibeCardSectionLabel: { marginTop: Spacing.xxl },
  flexShrinkLabel: { flexShrink: 1, minWidth: 0, marginTop: 0 },
  reflectBtn: {
    minHeight: 50,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  quotaIntro: { marginBottom: Spacing.xs },
  summaryHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  summaryControlFooter: {
    alignItems: "flex-end",
  },
  rangeToggle: { flexDirection: "row", flexShrink: 0, gap: Spacing.xs },
  rangePill: {
    paddingHorizontal: Spacing.md,
    paddingVertical: 5,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  dashboardControlButton: {
    minWidth: 42,
    minHeight: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  dashboardControlButtonPressed: {
    opacity: 0.78,
  },
  upsell: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  upsellCopy: { flex: 1, minWidth: 0, gap: 2 },
  dataRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    padding: Spacing.md,
    marginBottom: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  exportPanel: {
    gap: Spacing.md,
    padding: Spacing.md,
    marginBottom: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  exportPanelHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  exportCloseButton: {
    width: 34,
    height: 34,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 17,
    borderWidth: StyleSheet.hairlineWidth,
  },
  exportIntro: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.md,
  },
  exportGroup: { gap: Spacing.sm },
  exportChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.sm,
  },
  exportChip: {
    minHeight: 36,
    maxWidth: "100%",
    paddingHorizontal: Spacing.md,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  exportChipText: { fontWeight: "800" },
  exportCheckRow: {
    minHeight: 34,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
  },
  exportButton: {
    minHeight: 48,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
  },
  exportButtonPressed: { opacity: 0.84 },
  freqRow: { flexDirection: "row", flexWrap: "wrap", gap: Spacing.sm },
  freqChip: {
    minHeight: 38,
    maxWidth: "100%",
    paddingHorizontal: Spacing.md,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  freqChipText: { fontWeight: "700" },
  customReminder: { gap: Spacing.sm, marginTop: Spacing.sm },
  dayChip: {
    minWidth: 44,
    minHeight: 38,
    paddingHorizontal: Spacing.sm,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  goalsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  sectionActionHeader: {
    minHeight: 64,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  goalsHeaderCopy: { flex: 1, minWidth: 0, gap: 2 },
  goalTabs: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: Spacing.xs,
  },
  undoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  undoCopy: { flex: 1, minWidth: 0 },
  goalTab: {
    minHeight: 32,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: Spacing.xs,
  },
  goalTabPressed: { opacity: 0.72 },
  goalTabText: { fontWeight: "600" },
  goalTabTextActive: { fontWeight: "800" },
  goalTabSeparator: { paddingHorizontal: 2 },
  emptyGoals: {
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  newGoalButton: {
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    paddingHorizontal: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  goalRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.sm,
    minHeight: 50,
    paddingVertical: Spacing.sm,
    paddingLeft: Spacing.sm,
    paddingRight: Spacing.xs,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  goalRowArchived: { opacity: 0.72 },
  goalCircle: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
  },
  goalIconButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  // Top padding lines the first line of the goal up with the circle beside it.
  goalCopy: { flex: 1, minWidth: 0, gap: 3, paddingTop: 6 },
  goalText: { flexShrink: 1 },
  goalDone: { textDecorationLine: "line-through" },
  goalMenuCard: {
    width: "100%",
    maxWidth: 380,
    gap: Spacing.xs,
    padding: Spacing.md,
    borderRadius: Radius.xl,
    borderWidth: 1,
  },
  goalMenuRowDisabled: {
    // Greyed, not hidden: the row still explains that editing exists and that
    // Reopen is what unlocks it.
    opacity: 0.45,
  },

  goalMenuRow: {
    minHeight: 48,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    paddingHorizontal: Spacing.sm,
    borderRadius: Radius.md,
  },
  goalEditScreen: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: Spacing.lg,
  },
  goalEditBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.5)",
  },
  goalEditCard: {
    width: "100%",
    maxWidth: 440,
    // Capped so a goal with five notes scrolls inside the card instead of
    // pushing the Create/Save button off a small screen.
    maxHeight: "88%",
    gap: Spacing.md,
    padding: Spacing.lg,
    borderRadius: Radius.xl,
    borderWidth: 1,
  },
  goalEditScroll: { flexGrow: 0, flexShrink: 1 },
  goalEditScrollContent: { gap: Spacing.md, paddingBottom: Spacing.xs },
  goalFieldGroup: { gap: Spacing.sm },
  goalTitleInput: {
    minHeight: 64,
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.md,
    borderWidth: 1,
    fontSize: 15,
    lineHeight: 20,
  },
  goalEditHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  controlScreen: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: Spacing.lg,
  },
  controlBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.5)",
  },
  controlCard: {
    width: "100%",
    maxWidth: 420,
    gap: Spacing.md,
    padding: Spacing.lg,
    borderRadius: Radius.xl,
    borderWidth: 1,
  },
  controlHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  controlRow: {
    minHeight: 68,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  goalNotesList: { gap: Spacing.sm },
  goalNoteRow: {
    flexDirection: "row",
    // Top-aligned so the remove button stays level with the first line as the
    // field grows, rather than drifting down beside a two-line note.
    alignItems: "flex-start",
    gap: Spacing.sm,
  },
  goalNoteField: { flex: 1, minWidth: 0, gap: 2 },
  goalNoteInput: {
    minHeight: 42,
    // Vertical padding rather than a fixed height: a multiline field has to be
    // free to grow with its content.
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.md,
    borderWidth: 1,
    fontSize: 15,
    lineHeight: 20,
  },
  goalNoteCount: {
    alignSelf: "flex-end",
    paddingRight: Spacing.xs,
  },
  goalEditFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  goalAddNoteButton: {
    minHeight: 36,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.xs,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  goalSaveButton: {
    minHeight: 48,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.sm,
    borderRadius: Radius.lg,
  },
  emailRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  emailCopy: { flex: 1, minWidth: 0, gap: 2 },
});
